import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';

export const JoinView = ({ sessionId: propSessionId, onSessionJoined }) => {
  const [sessionId, setSessionId] = useState(propSessionId || '');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [sessionInfo, setSessionInfo] = useState(null);
  const [requiresPassword, setRequiresPassword] = useState(false);

  useEffect(() => {
    if (propSessionId) {
      setSessionId(propSessionId);
      checkSession(propSessionId);
    }
  }, [propSessionId]);

  const checkSession = async (id) => {
    if (!id) {
      setError('Please enter a session ID');
      return null;
    }

    if (!/^[a-zA-Z0-9]{8}$/.test(id)) {
      setError('Invalid session ID format. Must be 8 characters (letters and numbers only)');
      return null;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await api.getSessionInfo(id);

      if (response.error) {
        setError(response.error);
        return null;
      }

      setSessionInfo(response);
      setRequiresPassword(response.requiresPassword);
      return response;
    } catch (error) {
      const errorMessage = error.message || 'Failed to connect to session';
      setError(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const handleJoin = async (e) => {
    e.preventDefault();

    if (!sessionId) {
      setError('Please enter a session ID');
      return;
    }

    // Validate session ID format
    if (!/^[a-zA-Z0-9]{8}$/.test(sessionId)) {
      setError('Invalid session ID format. Must be 8 characters (letters and numbers only)');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      let info = sessionInfo;
      if (!info) {
        info = await checkSession(sessionId);
      }

      if (!info) {
        return;
      }

      if (info.requiresPassword) {
        if (!password) {
          setError('Password is required');
          return;
        }

        const verifyResponse = await api.verifySessionPassword(sessionId, password);
        if (verifyResponse.error) {
          setError(`Password Error: ${verifyResponse.error}`);
          return;
        }
      }

      const newUrl = `/join/${sessionId}`;
      window.history.pushState({}, '', newUrl);

      onSessionJoined({
        sessionId,
        ...info,
      });
    } catch (error) {
      const errorMessage = error.message || 'Failed to join session';
      setError(`Connection Error: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSessionIdChange = (e) => {
    const id = e.target.value.trim();

    // Validate session ID format (8-character alphanumeric)
    if (id && !/^[a-zA-Z0-9]{8}$/.test(id)) {
      setError('Session ID must be 8 characters (letters and numbers only)');
    } else if (error && error.includes('Session ID must be 8 characters')) {
      setError('');
    }

    setSessionId(id);

    // Also update the parent's session ID state so URL routing works
    if (id && /^[a-zA-Z0-9]{8}$/.test(id)) {
      // Call a function to update parent state if it exists
      // Update the parent's session ID
    }

    // If we have a valid session ID, automatically check it
    if (id && /^[a-zA-Z0-9]{8}$/.test(id)) {
      // Small delay to let user finish typing
      setTimeout(() => {
        if (sessionId === id && !sessionInfo) {
          checkSession(id);
        }
      }, 500);
    } else {
      // Reset session info if invalid ID
      setSessionInfo(null);
      setRequiresPassword(false);
    }
  };

  return (
    <div className="theme-orange">
      <div className="max-w-md mx-auto py-10 px-4 animate-fade-in-up">
      <div className="text-center mb-8">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl mx-auto mb-4" style={{background:'var(--primary-50)'}}>🔗</div>
        <h1 className="text-2xl font-bold mb-2 tracking-tight" style={{color: 'var(--text-primary)'}}>Join Session</h1>
        <p className="text-sm" style={{color: 'var(--text-secondary)'}}>Enter session ID or use the link you received</p>
      </div>

      <div className="card p-6" style={{boxShadow:'var(--shadow-lg)'}}>
          <form onSubmit={handleJoin} className="space-y-5">
            <div>
              <label htmlFor="sessionId" className="label">Session ID</label>
              <input
                type="text"
                id="sessionId"
                value={sessionId}
                onChange={handleSessionIdChange}
                placeholder="Enter 8-character session ID"
                className="input text-center font-mono font-bold text-lg tracking-widest"
                required
                disabled={propSessionId && sessionInfo}
                maxLength={8}
              />
            {propSessionId && (
              <p className="mt-2 text-sm text-gray-500">
                Session ID: {propSessionId}
              </p>
            )}
          </div>

          {requiresPassword && (
            <div>
              <label htmlFor="password" className="label">Session Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter session password"
                className="input"
                required
              />
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-100 rounded-xl p-3 animate-fade-in">
              <p className="text-red-600 text-sm font-medium flex items-center gap-2">⚠️ {error}</p>
            </div>
          )}

          {sessionInfo && (
            <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800/40 rounded-xl p-4 animate-scale-in">
              <h3 className="font-semibold text-emerald-800 dark:text-emerald-400 mb-2 text-sm">Session Found</h3>
              <div className="text-sm text-emerald-700 dark:text-emerald-300 space-y-1">
                <p className="flex justify-between"><span className="text-emerald-600 dark:text-emerald-500">Sender:</span> <span className="font-medium">{sessionInfo.senderName}</span></p>
                <p className="flex justify-between"><span className="text-emerald-600 dark:text-emerald-500">Files:</span> <span className="font-medium">{sessionInfo.fileCount}</span></p>
                <p className="flex justify-between"><span className="text-emerald-600 dark:text-emerald-500">Online:</span> <span className="font-medium">{sessionInfo.connectedClients} users</span></p>
                {requiresPassword && <p className="text-orange-600 font-medium mt-1">🔒 Password required</p>}
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => checkSession(sessionId)}
              disabled={!sessionId || isLoading || sessionInfo}
              className="btn-secondary flex-1 text-sm py-2.5"
            >
              {isLoading ? 'Checking...' : sessionInfo ? '✓ Valid' : 'Check'}
            </button>

            <button
              type="submit"
              disabled={!sessionId || isLoading || (requiresPassword && !password)}
              className="btn-primary flex-1 text-sm py-2.5"
            >
              {isLoading ? (
                <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> Joining...</>
              ) : sessionInfo ? 'Join Session →' : 'Validate & Join'}
            </button>
          </div>
        </form>
      </div>

      <p className="mt-6 text-center text-xs" style={{color: 'var(--text-muted)'}}>Ask the sender for the session ID or scan the QR code.</p>
    </div>
    </div>
  );
};